import React from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { StackView } from '@planningcenter/tapestry-react'
import {
  Code,
  CodeBlock as TypographyCodeBlock,
  Heading,
  Link as SharedLink,
  List,
  PageHeading,
  Text,
} from '../../../shared/typography'

export default function page() {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>OpenID Connect (OIDC)</PageHeading>

      <Text>
        <Link to="https://openid.net/developers/how-connect-works/" external>
          OpenID Connect
        </Link>{' '}
        is an authentication layer on top of OAuth 2.0 that allows users of your
        applications to authenticate using their Planning Center account.
      </Text>

      <Text>
        The OIDC flow is very similar to the typical OAuth 2.0 flow, but
        requires requesting an additional <Code>openid</Code> scope. In fact,
        the <Code>openid</Code> scope can be requested alongside other
        application scopes (see{' '}
        <Link to="/overview/authentication/oauth-2-0#scopes">
          Application Scopes
        </Link>
        ).
      </Text>

      <Text>
        When requesting the <Code>openid</Code> scope, you'll receive an{' '}
        <Code>id_token</Code> (a JWT) in addition to the standard OAuth 2.0{' '}
        <Code>access_token</Code>.{' '}
        <Link
          to="https://openid.net/specs/openid-connect-core-1_0.html#IDToken"
          external
        >
          ID Tokens
        </Link>{' '}
        contain identity claim assertions for the currently logged in user.
      </Text>

      <Text>
        ID tokens will expire after 1 hour from the time they are issued.
      </Text>

      <Heading anchored id="oidc-discovery" size={1}>
        OIDC Discovery
      </Heading>

      <Text>
        Planning Center supports OIDC discovery through the standard OpenID
        configuration endpoint:
      </Text>

      <CodeBlock language="bash">
        {
          'https://api.planningcenteronline.com/.well-known/openid-configuration'
        }
      </CodeBlock>

      <Text>
        This endpoint provides all the necessary configuration information
        (including supported scopes, token endpoints, and token signing
        information) and simplifies the configuration process for OIDC clients
        that support server discovery.
      </Text>

      <Heading anchored id="oidc-parameters" size={1}>
        Additional OIDC Request Parameters
      </Heading>

      <Text>
        When initiating the OIDC authorization flow, the following request
        parameters are optional but strongly recommended, and should be used in
        addition to the standard OAuth flow request parameters:
      </Text>

      <List>
        <List.Item>
          <Text>
            <Code>state</Code> - A unique, unguessable string used to prevent
            CSRF attacks. Your application should verify this value matches the
            originally sent value when the user is redirected back to your
            application.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Code>prompt</Code> - Defines what the login process will look like
            for the user. <Code>select_account</Code> is recommended to allow
            users to verify which account they will be signing in as and allow
            them to switch accounts. Alternatively <Code>login</Code> will log
            users out of their current Planning Center session and force them to
            login again.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Code>nonce</Code> - A unique string that will be included in the ID
            token to prevent replay attacks. If provided, your application
            should verify this value matches what was originally sent.
          </Text>
        </List.Item>
      </List>

      <Heading anchored id="oidc-workflow" size={1}>
        OIDC Workflow
      </Heading>

      <List ordered>
        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              Follow steps 1-3 from the{' '}
              <Link to="/overview/authentication/oauth-2-0">
                OAuth 2.0 section
              </Link>{' '}
              to initiate the authorization flow, with two key differences:
            </Text>

            <List>
              <List.Item marginBottom={0}>
                <Text>
                  Include the <Code>openid</Code> scope
                </Text>
              </List.Item>

              <List.Item marginBottom={0}>
                <Text>
                  Include the recommended OIDC parameters:{' '}
                  <Code>RANDOM_NONCE_VALUE</Code> and{' '}
                  <Code>RANDOM_STATE_VALUE</Code>
                  <Code>openid</Code> scope
                </Text>
              </List.Item>
            </List>

            <Text>The authorize redirect should look something like this:</Text>

            <CodeBlock>
              {
                'https://api.planningcenteronline.com/oauth/authorize?client_id=CLIENT_ID&redirect_uri=https://example.com/auth/complete&response_type=code&scope=openid&state=RANDOM_STATE_VALUE&nonce=RANDOM_NONCE_VALUE&prompt=select_account'
              }
            </CodeBlock>

            <Text>
              Replace <Code>CLIENT_ID</Code>, <Code>RANDOM_NONCE_VALUE</Code>,{' '}
              <Code>RANDOM_STATE_VALUE</Code> and{' '}
              <Code>https://example.com/auth/complete</Code> with your actual
              application’s values. If you need additional scopes, add them to{' '}
              <Code>scope=openid</Code> appropriately (see{' '}
              <Link to="/overview/authentication/oauth-2-0#scopes">
                Application Scopes
              </Link>
              ).
            </Text>
          </StackView>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              After exchanging the authorization code for tokens, you'll receive
              an <Code>access_token</Code>, <Code>refresh_token</Code>, and <Code>id_token</Code>.
              The <Code>id_token</Code> is a JWT that contains basic user and
              organization information for the currently logged in user:
            </Text>

            <CodeBlock language="json">
              {JSON.stringify(
                {
                  iss: 'https://api.planningcenteronline.com',
                  sub: '1234567890',
                  aud: '<your client id>',
                  name: 'John Doe',
                  email: 'john.doe@planningcenter.com',
                  organization_id: 123456,
                  organization_name: 'Test Church',
                  nonce: '<your originally passed nonce value>',
                  exp: 1755801744,
                  iat: 1755798144,
                },
                null,
                2
              )}
            </CodeBlock>
          </StackView>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              Refetch user data: To get the most up-to-date current user
              information, make a request to the <Code>/oauth/userinfo</Code>{' '}
              endpoint:
            </Text>
            <CodeBlock language="bash">
              {
                "curl -H 'Authorization: Bearer pco_tok_97df13b9' https://api.planningcenteronline.com/oauth/userinfo"
              }
            </CodeBlock>
            <Text>
              This will return the same users identity claims as the{' '}
              <Code>id_token</Code>:
            </Text>
            <CodeBlock language="json">
              {JSON.stringify(
                {
                  sub: '1234567890',
                  name: 'John Doe',
                  email: 'john.doe@planningcenter.com',
                  organization_id: 123456,
                  organization_name: 'Test Church',
                },
                null,
                2
              )}
            </CodeBlock>
          </StackView>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              An expired <Code>id_token</Code> can be refreshed to extend a user's session (see{' '}
                <Link to="/overview/authentication/oauth-2-0#refreshing-a-token">
                  Refresh Tokens
                </Link>
                )
            </Text>
          </StackView>
        </List.Item>
      </List>
    </StackView>
  )
}




function Link({ external, ...props }) {
  return external ? (
    <SharedLink external {...props} />
  ) : (
    <ReactRouterLink component={SharedLink} {...props} />
  )
}

function CodeBlock(props) {
  return (
    <TypographyCodeBlock
      customStyle={{ marginTop: 0, marginBottom: 0 }}
      {...props}
    />
  )
}
